import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import ShipmentTable from "component/flex/shipments/ShipmentTable";
import RequestShipmentModal from "component/flex/shipments/modals/RequestShipmentModal";
import { useQuery, keepPreviousData } from "react-query";
import {
    handleGetAllAssignedShipments,
    handleGetAllShipments,
    handleGetAllShipmentsStats,
} from "api/fetchers";
import { AddIcon } from "asset/icons";
import { debounce } from "lodash";
import { useSelector } from "react-redux";
import RatingModal from "dashboard/component/rating";
import { useQueryState } from "nuqs";

const metricCardsList = [
    { title: "All Shipments", key: "allShipments" },
    { title: "Not Started", key: "notStarted" },
    { title: "In-Progress", key: "inProgress" },
    { title: "Cancelled", key: "cancelled" },
    { title: "Completed", key: "completed" },
];

const Shipment = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const business = useSelector((state) => state?.business);
    const [searchQuery, setSearchQuery] = useState("");
    const [showRating, setShowRating] = useState(false);

    const isBusinessApproved = business?.status === "APPROVED";
    const [shipmentSubTab, setShipmentSubTab] = useQueryState("endpoint", {
        defaultValue: "list-shipment",
    });
    const [activeTab, setActiveTab] = useQueryState("activeTab", {
        defaultValue: "Created",
    });

    const handleCloseModal = () => setShowModal(false);

    const { data: statsData, isLoading: loadingStats } = useQuery({
        queryFn: handleGetAllShipmentsStats,
        queryKey: ["get-shipments-stats"],
        placeholderData: keepPreviousData,
    });

    const { data, isLoading, refetch } = useQuery({
        queryFn: () =>
            handleGetAllShipments({
                endpoint: shipmentSubTab,
                searchTerm: searchQuery,
                currentPage,
            }),
        queryKey: [
            "get-all-shipments",
            shipmentSubTab,
            searchQuery,
            business?._id,
            activeTab,
            currentPage,
        ],
        placeholderData: keepPreviousData,
    });
    const {
        data: assignedShipmentData,
        isLoading: loadingAssignedLoading,
        refetch: refetchAssignedShipment,
    } = useQuery({
        queryFn: () =>
            handleGetAllAssignedShipments({
                endpoint: shipmentSubTab,
                searchTerm: searchQuery,
                currentPage,
            }),
        queryKey: [
            "get-all-assigned-shipments",
            shipmentSubTab,
            searchQuery,
            business?._id,
            activeTab,
            currentPage,
        ],
        placeholderData: keepPreviousData,
    });

    useEffect(() => {
        if (sessionStorage.getItem("showShipmentRating") === "true") {
            setTimeout(() => {
                setShowRating(true);
            }, 3000);
            sessionStorage.removeItem("showShipmentRating");
        }
    }, []);

    const shipmentData = data?.shipments?.map((item) => {
        return {
            ...item,
            id: item?._id,
            trackingNumber: item?.generatedTitle || item?.shipmentTitle,
            origin: item?.shippingDetails?.[0]?.departurePort?.address
                ? item?.shippingDetails?.[0]?.departurePort?.address
                : item?.shippingDetails?.[0]?.departurePort?.address ||
                  item?.shippingDetails?.[0]?.departurePort ||
                  "N/A",
            destination: item?.shippingDetails?.at(-1)?.destinationPort?.address
                ? item?.shippingDetails?.at(-1)?.destinationPort?.address
                : item?.shippingDetails?.[0]?.destinationPort?.address ||
                  item?.shippingDetails?.[0]?.destinationPort ||
                  "N/A",
            status: item?.shipmentStatus,
            receiver: item?.receiverName,
            date: item?.createdAt,
            shipmentTitle: item?.shipmentTitle,
            details: item?.shippingDetails,
        };
    });

    const transformedAssignedShipmentData = assignedShipmentData?.shipments?.map((item) => {
        return {
            ...item,
            id: item?._id,
            trackingNumber: item?.generatedTitle || item?.shipmentTitle,
            origin: item?.shippingDetails?.[0]?.departurePort?.address
                ? item?.shippingDetails?.[0]?.departurePort?.address
                : item?.shippingDetails?.[0]?.departurePort?.address ||
                  item?.shippingDetails?.[0]?.departurePort ||
                  "N/A",
            destination: item?.shippingDetails?.at(-1)?.destinationPort?.address
                ? item?.shippingDetails?.at(-1)?.destinationPort?.address
                : item?.shippingDetails?.[0]?.destinationPort?.address ||
                  item?.shippingDetails?.[0]?.destinationPort ||
                  "N/A",
            status: item?.shipmentStatus,
            receiver: item?.receiverName,
            date: item?.createdAt,
            shipmentTitle: item?.shipmentTitle,
            details: item?.shippingDetails,
        };
    });

    const summaryData = useMemo(() => {
        return {
            allShipments: statsData?.AllShipmentCount?.allShipment,
            notStarted: statsData?.NotStarted?.notStarted,
            inProgress: statsData?.InProgress?.inProgress,
            cancelled: statsData?.CanCelled?.cancelled,
            completed: statsData?.Completed?.completed,
        };
    }, [statsData]);

    const tabs = ["Created", "Assigned"];
    const subTabs = [
        { text: "All", endpoint: "list-shipment" },
        { text: "Not Started", endpoint: "notstarted" },
        { text: "In-Progress", endpoint: "progress" },
        { text: "Cancelled", endpoint: "cancelled" },
        { text: "Completed", endpoint: "completed" },
        { text: "Drafts", endpoint: "draft" },
    ];

    const handleSearchChange = useCallback(
        debounce((searchString) => {
            setSearchQuery(searchString.trim().toLowerCase());
        }, 700),
        []
    );
    const updateSearchString = (searchString) => {
        setSearchTerm(searchString.trim().toLowerCase());
        handleSearchChange(searchString);
    };

    const isAssignedTab = activeTab === "Assigned";
    const shipmentDataToDisplay = isAssignedTab ? transformedAssignedShipmentData : shipmentData;
    const totalPages = isAssignedTab ? assignedShipmentData?.totalPages : data?.totalPages;
    const isLoadingShipment = isLoading || loadingAssignedLoading;
    const refetchDataFunc = isAssignedTab ? refetchAssignedShipment : refetch;

    return (
        <div className="min-h-screen  mt-3 ">
            {/* Header Section */}
            <div className="flex flex-col items-start justify-between gap-2 mb-5 md:items-center md:flex-row md:flex-nowrap">
                <h2 className="font-[450] text-[22px] text-main-dark md:max-800:mt-2 translate-y-2.5">
                    Shipments
                </h2>
                <div className="flex gap-4">
                    {/* This will be uncommented once the functionality and API has been discussed thoroughly */}
                    {/* <button
                        onClick={handleOpenModal}
                        className="flex gap-x-1 items-center bg-[#F4E9FF] text-[#6F00FF] rounded-lg py-2 px-4 md:!px-4 text-sm"
                    >
                        <LinkIconSvg />

                        <span className="hidden md:block">Request shipment details</span>
                    </button> */}
                    <Link
                        to={isBusinessApproved ? "/flex/shipments/create" : "#"}
                        className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-1 text-white rounded-lg py-[10px] px-3 md:!px-4 text-sm ${
                            !isBusinessApproved ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                    >
                        <AddIcon className=" stroke-white fill-white size-4" />
                        <span>Create shipment</span>
                    </Link>
                </div>
            </div>

            {/* Analytics Section */}
            <div className="sm:mx-10 md:mx-0 mb-2 mt-5 grid grid-cols-[repeat(5,minmax(280px,1fr))] gap-6 overflow-x-auto [scrollbar-width:none]">
                {metricCardsList?.map(({ title, key }) => (
                    <div
                        key={title + key}
                        className={`bg-white !border !border-[#F2F3FC] rounded-lg !p-4 h-[124px] flex flex-col justify-between`}
                    >
                        <p className="text-sm font-medium text-main-grey">{title}</p>
                        <h3 className="text-[32px] font-normal mb-0 !text-main">
                            {loadingStats ? "--" : summaryData[key]?.toString() || "0"}
                        </h3>
                    </div>
                ))}
            </div>

            {/* Table Section */}
            <ShipmentTable
                filteredShipments={[]}
                paginatedShipments={shipmentDataToDisplay}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pageSize={totalPages}
                tabs={tabs}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                subTabs={subTabs}
                shipmentSubTab={shipmentSubTab}
                setShipmentSubTab={setShipmentSubTab}
                searchTerm={searchTerm}
                setSearchTerm={updateSearchString}
                isLoading={isLoadingShipment}
                refetchShipments={refetchDataFunc}
                notActiveBusiness={isBusinessApproved}
            />

            {/* Modal */}
            <RequestShipmentModal show={showModal} onClose={handleCloseModal} />
            <RatingModal
                isOpen={showRating}
                onClose={() => setShowRating(false)}
                userId={business?._id}
                reviewType="Shipment"
                title="How was your experience creating a new shipment?"
                subtitle="We’d like to know how satisfied you are with the process"
                firebaseEventName="shipment_feedback_rating"
                featureVersion="shipment_v1"
            />
        </div>
    );
};

export default Shipment;
